import { ChevronLeft, ChevronRight, EyeOff, Play, RefreshCw } from 'lucide-react'

import { ActionFieldset, ActionInfo, Button, CheckboxField, ChoiceChips, SelectField, StatusMessage, TextField, formatAmount } from '@krp/ui-kit'

import { CHANGE_LABELS, DATA_KIND_LABELS, IGNORE_LEVEL_LABELS, detailOf, formatMoment, subjectOf, vatText } from '../model/labels'
import { CHANGE_TYPES, DATA_KINDS, type ChangeType, type DataKind, type Divergence, type IgnoreLevel, type ShownError, type SourceValue } from '../model/types'
import { ApplyPanel, type ApplyActions, type ApplyVm } from './ApplyPanel'
import { ChangeBadge, ErrorBlock } from './parts'
import './sync.css'

/** Источник, которым можно назвать значение и который можно выбрать целью. */
export interface SourceRef {
  resourceId: string
  name: string
}

export interface DivergenceFilter {
  type: ChangeType | null
  dataKind: DataKind | null
  query: string
}

export interface DivergencesVm {
  /** Ресурс или `catalog`. */
  source: string
  sourceName: string
  /** Окно общего каталога: в нём только позиции без связей, пометка одна. */
  catalog: boolean
  filter: DivergenceFilter
  status: 'loading' | 'failed' | 'ready'
  loadError: ShownError | null
  refreshing: boolean
  page: number
  pages: number
  total: number
  items: Divergence[]
  nameOf: (resourceId: string) => string
  /** Адрес кадра фото стороны (строки фото): у значения без фото — `null`. */
  photoSrc: (value: SourceValue) => string | null
  selectedIds: ReadonlySet<number>
  /** Источники, куда можно применить отмеченное или где перестать его отслеживать. */
  targetChoices: SourceRef[]
  targets: readonly string[]
  ignoreLevel: IgnoreLevel
  /** «Не отслеживать» идёт по строке на пару «строка × источник»: сколько отправлено из скольких. */
  ignoreProgress: { done: number; total: number } | null
  ignoreErrors: ShownError[]
  /** Итог последней команды словами: «Больше не отслеживается: 12». */
  notice: string | null
  /** Окно применения открыто поверх списка. */
  apply: ApplyVm | null
}

export interface DivergencesActions {
  filterChanged: (filter: DivergenceFilter) => void
  pageChanged: (page: number) => void
  rowToggled: (id: number) => void
  pageSelected: (selected: boolean) => void
  targetToggled: (resourceId: string) => void
  openApply: () => void
  ignoreLevelChanged: (level: IgnoreLevel) => void
  ignore: () => void
  reload: () => void
}

const ALL = 'all'
const ROW_LEVELS: readonly IgnoreLevel[] = ['record', 'product']

/** Фото стороны: снимок и начало отпечатка (одно ли это фото, решает содержимое); фото нет — словом, а не пустым местом. */
function PhotoValue({ value, src, name }: { value: SourceValue; src: string | null; name: string }) {
  if (value.value === null || src === null) return <span className="sync-values__absent">нет фото</span>
  return (
    <span className="sync-photo">
      <img className="sync-photo__image" src={src} alt={`Фото в «${name}»`} loading="lazy" width={96} height={96} />
      <span className="sync__note">{value.value.slice(0, 12)}</span>
    </span>
  )
}

function ValueText({ value, dataKind }: { value: SourceValue; dataKind: DataKind }) {
  if (!value.present) return <span className="sync-values__absent">нет</span>
  if (dataKind === 'price') {
    const vat = vatText(value.vatPercent)
    return (
      <span>
        <span className="sync-apply__price">{value.value === null ? 'нет цены' : formatAmount({ decimal: value.value })}</span>
        {(value.priceTypeName ?? value.priceTypeKey) !== null && <span className="sync__note"> · {value.priceTypeName ?? value.priceTypeKey}</span>}
        {vat !== null && <span className="sync__note"> · {vat}</span>}
      </span>
    )
  }
  return <span>{value.value ?? '—'}</span>
}

function DivergenceRow({ item, vm, actions }: { item: Divergence; vm: DivergencesVm; actions: DivergencesActions }) {
  const selected = vm.selectedIds.has(item.id)
  const detail = detailOf(item)
  return (
    <li className="sync-item" data-selected={selected}>
      <CheckboxField label={<span className="sync__sr">Отметить «{item.title}»</span>} checked={selected} onChange={() => actions.rowToggled(item.id)} actionId="sync.divergences.select-row" />
      <div>
        <div className="sync-item__head">
          <ChangeBadge change={item.type} subject={subjectOf(item)} />
          <span className="sync-item__name">{item.title}</span>
          {detail !== null && <span className="sync__note">{detail}</span>}
        </div>
        <dl className="sync-values">
          {item.values.map((value) => (
            <div key={value.resourceId} className="sync-values__pair">
              <dt>{vm.nameOf(value.resourceId)}</dt>
              <dd>
                {item.dataKind === 'photo' ? (
                  <PhotoValue value={value} src={vm.photoSrc(value)} name={vm.nameOf(value.resourceId)} />
                ) : (
                  <ValueText value={value} dataKind={item.dataKind} />
                )}
              </dd>
            </div>
          ))}
        </dl>
        <p className="sync__note">Найдено {formatMoment(item.detectedAt)}</p>
      </div>
    </li>
  )
}

function Filters({ vm, actions }: { vm: DivergencesVm; actions: DivergencesActions }) {
  const chips = [{ id: ALL, label: 'Все' }, ...CHANGE_TYPES.filter((type) => type !== 'only-common').map((type) => ({ id: type, label: CHANGE_LABELS[type] }))]
  return (
    <div className="sync__panel" role="search" aria-label="Фильтры расхождений">
      {!vm.catalog && (
        <ActionInfo id="sync.divergences.filter-change" label="Что показывать">
          <ChoiceChips
            label="Что показывать"
            items={chips}
            value={vm.filter.type ?? ALL}
            onChange={(id) => actions.filterChanged({ ...vm.filter, type: CHANGE_TYPES.find((type) => type === id) ?? null })}
          />
        </ActionInfo>
      )}
      <div className="sync__row">
        <SelectField
          label="Вид данных"
          value={vm.filter.dataKind ?? ALL}
          options={[{ value: ALL, label: 'Все виды' }, ...DATA_KINDS.map((kind) => ({ value: kind, label: DATA_KIND_LABELS[kind] }))]}
          onChange={(value) => actions.filterChanged({ ...vm.filter, dataKind: DATA_KINDS.find((kind) => kind === value) ?? null })}
          actionId="sync.divergences.filter-kind"
        />
        <TextField label="Найти: название, артикул, штрихкод" type="search" value={vm.filter.query} onChange={(query) => actions.filterChanged({ ...vm.filter, query })} actionId="sync.divergences.search" />
      </div>
    </div>
  )
}

function ActionBar({ vm, actions }: { vm: DivergencesVm; actions: DivergencesActions }) {
  const nothing = vm.selectedIds.size === 0 ? 'Отметьте строки слева.' : null
  const noTarget = vm.targets.length === 0 ? 'Отметьте источники.' : null
  const busy = vm.ignoreProgress !== null && vm.ignoreProgress.done < vm.ignoreProgress.total
  return (
    <section className="sync__panel sync-actions" aria-label="Что сделать с отмеченными">
      <p className="sync__note" aria-live="polite">
        Отмечено строк: <span className="sync__count">{vm.selectedIds.size}</span>
      </p>
      {vm.targetChoices.length > 0 && (
        <ActionFieldset actionId="sync.divergences.target" legend="Куда применить или где перестать отслеживать">
          <div className="sync__row">
            {vm.targetChoices.map((target) => (
              <CheckboxField key={target.resourceId} label={target.name} checked={vm.targets.includes(target.resourceId)} onChange={() => actions.targetToggled(target.resourceId)} />
            ))}
          </div>
        </ActionFieldset>
      )}
      <div className="sync__row">
        <Button variant="primary" icon={<Play size={16} aria-hidden="true" />} disabled={nothing !== null} disabledReason={nothing} onClick={actions.openApply} actionId="sync.divergences.apply">
          Применить…
        </Button>
        <SelectField
          label="Не отслеживать"
          value={vm.ignoreLevel}
          options={ROW_LEVELS.map((level) => ({ value: level, label: IGNORE_LEVEL_LABELS[level] }))}
          onChange={(value) => actions.ignoreLevelChanged(ROW_LEVELS.find((level) => level === value) ?? 'record')}
          actionId="sync.divergences.ignore-level"
        />
        <Button
          variant="secondary"
          icon={<EyeOff size={16} aria-hidden="true" />}
          busy={busy}
          disabled={nothing !== null || noTarget !== null}
          disabledReason={nothing ?? noTarget}
          onClick={actions.ignore}
          actionId="sync.divergences.ignore"
        >
          Не отслеживать
        </Button>
      </div>
      {vm.ignoreProgress !== null && busy && (
        <p className="sync__note" role="status">
          Отправлено {vm.ignoreProgress.done} из {vm.ignoreProgress.total}…
        </p>
      )}
      {vm.ignoreErrors.map((error, index) => (
        <ErrorBlock key={`${error.title}:${index}`} error={error} />
      ))}
      {vm.notice !== null && (
        <p className="sync__note" role="status">
          {vm.notice}
        </p>
      )}
    </section>
  )
}

/**
 * Окно расхождений одного источника или общего каталога (RP-SYN8, требования 6, 9, 10, 12): один список с пометками
 * «добавлено / изменено / удалено / только в каталоге» и тем, чего строка касается; фильтры; отметки слева и «выбрать все»;
 * выбор источников; «Применить…» и «Не отслеживать». Применение открывается поверх списка (`ApplyPanel`).
 */
export function DivergencesScreen({ vm, actions, apply }: { vm: DivergencesVm; actions: DivergencesActions; apply: ApplyActions }) {
  if (vm.apply !== null) return <ApplyPanel vm={vm.apply} actions={apply} />
  const allOnPage = vm.items.length > 0 && vm.items.every((item) => vm.selectedIds.has(item.id))
  return (
    <section aria-labelledby="sync-divergences-title" className="sync">
      <header>
        <h2 id="sync-divergences-title">Расхождения: {vm.sourceName}</h2>
        <p className="sync__lead">
          {vm.catalog
            ? 'Здесь позиции, которых нет ни в одном источнике: при переинициализации каталога они исчезнут. Отметьте, какие и куда добавить, или перестаньте их отслеживать.'
            : 'Здесь всё, чем этот источник расходится с остальными. Отметьте строки, выберите, куда их применить, и нажмите «Применить…». То, что никуда переносить не нужно, отметьте «Не отслеживать».'}
        </p>
      </header>

      <Filters vm={vm} actions={actions} />

      {vm.status === 'loading' && <StatusMessage kind="loading" title="Загружаем расхождения…" />}
      {vm.status === 'failed' && vm.loadError !== null && (
        <ErrorBlock
          error={vm.loadError}
          action={
            <Button variant="secondary" icon={<RefreshCw size={16} aria-hidden="true" />} onClick={actions.reload} actionId="sync.divergences.reload">
              Повторить
            </Button>
          }
        />
      )}
      {vm.status === 'ready' && (
        <>
          <div className="sync__row">
            <CheckboxField
              label={`Выбрать все на странице (${vm.items.length})`}
              checked={allOnPage}
              disabled={vm.items.length === 0}
              disabledReason={vm.items.length === 0 ? 'На странице нет строк.' : null}
              onChange={(checked) => actions.pageSelected(checked)}
              actionId="sync.divergences.select-all"
            />
            <p className="sync__note" aria-live="polite">
              Строк по фильтру: <span className="sync__count">{vm.total}</span>
              {vm.refreshing && ' · обновляется…'}
            </p>
          </div>
          {vm.items.length === 0 ? (
            <StatusMessage kind="empty" title="Расхождений по этому фильтру нет." />
          ) : (
            <ul className="sync-list" aria-label="Расхождения">
              {vm.items.map((item) => (
                <DivergenceRow key={item.id} item={item} vm={vm} actions={actions} />
              ))}
            </ul>
          )}
          {vm.pages > 1 && (
            <nav className="sync__row" aria-label="Страницы расхождений">
              <Button
                variant="secondary"
                icon={<ChevronLeft size={16} aria-hidden="true" />}
                disabled={vm.page <= 1}
                disabledReason={vm.page <= 1 ? 'Это первая страница.' : null}
                onClick={() => actions.pageChanged(vm.page - 1)}
                actionId="sync.divergences.page"
              >
                Назад
              </Button>
              <span aria-current="page">
                Страница {vm.page} из {vm.pages}
              </span>
              <Button
                variant="secondary"
                icon={<ChevronRight size={16} aria-hidden="true" />}
                disabled={vm.page >= vm.pages}
                disabledReason={vm.page >= vm.pages ? 'Это последняя страница.' : null}
                onClick={() => actions.pageChanged(vm.page + 1)}
                actionId="sync.divergences.page"
              >
                Дальше
              </Button>
            </nav>
          )}
        </>
      )}

      <ActionBar vm={vm} actions={actions} />
    </section>
  )
}
